import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { getHelpAssistantRequestMessages } from '@/app/helpArticle/_helpers';

import { usePostHelpArticleAsk } from '@/data/helpArticle';

/**
 * A conversation with the help centre assistant, held in state and never
 * stored. `messages` are the answered turns; the question being answered, or
 * the one that failed, sits apart in `pending` until it has an answer, so a
 * failed question is never sent as part of the next one.
 */
export const useHelpAssistantConversation = () => {
  const postHelpArticleAsk = usePostHelpArticleAsk();
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState(null);

  const ask = async (question) => {
    setPending({ question, isThinking: true, error: null });
    try {
      const result = await postHelpArticleAsk(getHelpAssistantRequestMessages(messages, question));
      setMessages((current) => [...current, { role: 'user', content: question }, { role: 'assistant', content: result.answer, sources: result.sources }]);
      setPending(null);
    } catch (error) {
      setPending({ question, isThinking: false, error });
    }
  };

  const retry = () => ask(pending.question);

  const reset = () => {
    setMessages([]);
    setPending(null);
  };

  return { messages, pending, ask, retry, reset };
};

/**
 * Whether the assistant's panel is open. It is opened on one page and counts as
 * closed once the reader leaves it — following a source link from the panel is
 * leaving it, and the article should not open with the panel over it.
 */
export const useHelpAssistantPanel = () => {
  const location = useLocation();
  const [openOnPath, setOpenOnPath] = useState(null);

  const open = () => setOpenOnPath(location.pathname);
  const close = () => setOpenOnPath(null);

  return { isOpen: openOnPath === location.pathname, open, close };
};
